import { computed, inject, Injector, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { LanguageId, VolunteerSignupMentorDetails, VolunteerSignupMentorStatusEnum } from "../../../../interfaces";


export interface MentorSignUpEditorFields {
	volunteerSignupMentorStatus: VolunteerSignupMentorStatusEnum,
	assignedLanguageId: LanguageId | undefined,
	note: string
}

export class MentorSignupEditorForm {

	private readonly injector = inject(Injector)
	public readonly commonForm = new CommonForm<MentorSignUpEditorFields>(this.injector);
	public mentorSignUpData = signal<VolunteerSignupMentorDetails | undefined>(undefined);


	private readonly statusOptions = computed(() => Object.entries(VolunteerSignupMentorStatusEnum).map(([key, value]) => ({ value, text: key })));

	readonly status = computed<CommonSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Status',
			options: this.statusOptions(),
			isRequired: true,
			isSpanish: false
		}),
		actualValue: computed(() => this.mentorSignUpData()?.volunteerSignupMentorStatus || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'volunteerSignupMentorStatus',
		form: this.commonForm,
	}));



	private readonly languageOptions = computed(() => {
		const interestedLanguages = [];
		if (this.mentorSignUpData()?.canVolunteerInEnglish)
			interestedLanguages.push({ value: LanguageId.English, text: "English" });
		if (this.mentorSignUpData()?.canVolunteerInSpanish)
			interestedLanguages.push({ value: LanguageId.Espanol, text: "Spanish" });
		return interestedLanguages;
	});
	readonly language = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number | undefined>>({
			label: 'Language',
			options: this.languageOptions(),
			isRequired: true,
			isSpanish: false
		}),
		isExcluded: computed(() => !this.isAssignedStatus()),
		actualValue: computed(() => this.mentorSignUpData()?.assignedLanguageId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'assignedLanguageId',
		form: this.commonForm,
	}));

	private isAssignedStatus = computed(() => {
		const status = this.status().pendingValue() ?? this.status().actualValue();
		return status === VolunteerSignupMentorStatusEnum.Assigned;
	});



	readonly note = computed<CommonTextField>(() => ({
		config: signal({ label: 'Note', min: 0, fixCase: 'sentence-case', isRequired: false, multiLine: true, isSpanish: false }),
		actualValue: computed(() => this.mentorSignUpData()?.note || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'note',
		form: this.commonForm,
	}));

}